import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import {
  Box, Typography, Button, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField,
} from '@mui/material';

const AdminDisputeCenter = ({ disputes, onResolved }) => {
  const navigate = useNavigate();
  const [resolveDialog, setResolveDialog] = useState({ open: false, itemId: null });
  const [resolveNotes, setResolveNotes] = useState('');
  const [error, setError] = useState('');

  const handleOpenResolve = (itemId) => {
    setResolveNotes('');
    setResolveDialog({ open: true, itemId });
  };

  const handleCloseResolve = () => {
    setResolveDialog({ open: false, itemId: null });
    setResolveNotes('');
    setError('');
  };

  const handleResolve = async () => {
    if (!resolveNotes.trim()) return;
    try {
      await axios.post(`/api/items/${resolveDialog.itemId}/resolve-dispute`, { resolution: resolveNotes });
      handleCloseResolve();
      onResolved();
    } catch (err) {
      setError('Failed to resolve dispute');
    }
  };

  if (disputes.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">No active disputes</Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>All items are currently dispute-free</Typography>
      </Box>
    );
  }

  return (
    <>
      {error && <Typography color="error" mb={2}>{error}</Typography>}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell><strong>Item</strong></TableCell>
              <TableCell><strong>Project</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Cost</strong></TableCell>
              <TableCell><strong>Disputed By</strong></TableCell>
              <TableCell><strong>Reason</strong></TableCell>
              <TableCell><strong>Disputed At</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {disputes.map((dispute) => (
              <TableRow key={dispute.id} hover sx={{ backgroundColor: '#fff3e0' }}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{dispute.title}</Typography>
                    <Chip
                      label={dispute.type}
                      size="small"
                      color={dispute.type === 'labor' ? 'primary' : 'warning'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{dispute.project_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Contractor: {dispute.contractor_first_name} {dispute.contractor_last_name}
                    </Typography>
                    {dispute.homeowner_first_name && (
                      <>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Homeowner: {dispute.homeowner_first_name} {dispute.homeowner_last_name}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{dispute.room_name}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    ${parseFloat(dispute.cost).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {dispute.disputed_by_first_name} {dispute.disputed_by_last_name}
                    </Typography>
                    <Chip
                      label={dispute.disputed_by_role}
                      size="small"
                      color={
                        dispute.disputed_by_role === 'contractor' ? 'primary' :
                        dispute.disputed_by_role === 'homeowner' ? 'secondary' : 'error'
                      }
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>{dispute.dispute_reason}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{new Date(dispute.disputed_at).toLocaleDateString()}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(dispute.disputed_at).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button variant="contained" color="success" size="small" onClick={() => handleOpenResolve(dispute.id)}>
                      Resolve
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => navigate(`/project/${dispute.project_id}`)}>
                      View Project
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={resolveDialog.open} onClose={handleCloseResolve} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Resolution Notes"
            multiline
            rows={4}
            fullWidth
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResolve}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleResolve} disabled={!resolveNotes.trim()}>
            Confirm Resolution
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminDisputeCenter;
